import { addMonths } from 'date-fns';

import type { TimeUnit } from './formats';

const MS_PER = {
    second: 1000,
    minute: 60 * 1000,
    hour: 60 * 60 * 1000,
    day: 24 * 60 * 60 * 1000,
} as const;

/**
 * Months from `earlier` to `later`, fractional: whole months by the calendar, then the
 * rest as a share of the month it falls in. A month from the 31st ends on the next
 * month's last day, so 31 January to 29 February is exactly one month.
 */
function monthsBetween(later: Date, earlier: Date): number {
    if (later.getDate() < earlier.getDate()) return -monthsBetween(earlier, later);

    const whole =
        (earlier.getFullYear() - later.getFullYear()) * 12 +
        (earlier.getMonth() - later.getMonth());
    const anchor = addMonths(later, whole).getTime();
    const rest = earlier.getTime() - anchor;
    const neighbour = addMonths(later, rest < 0 ? whole - 1 : whole + 1).getTime();
    const share = rest < 0 ? rest / (anchor - neighbour) : rest / (neighbour - anchor);

    return -(whole + share);
}

/**
 * Whole `unit`s from `earlier` to `later`, truncated toward zero (negative when `later`
 * is the earlier one).
 *
 * A day is 24 hours of wall-clock time: the hour a DST switch adds or removes between the
 * two is taken out first, so midnight to midnight across a switch is a whole day.
 */
export function wholeUnitsBetween(later: Date, earlier: Date, unit: TimeUnit): number {
    if (unit === 'month' || unit === 'year') {
        const months = monthsBetween(later, earlier);
        return Math.trunc(unit === 'year' ? months / 12 : months) || 0;
    }

    let elapsed = later.getTime() - earlier.getTime();
    if (unit === 'day') {
        const zoneShift = (later.getTimezoneOffset() - earlier.getTimezoneOffset()) * MS_PER.minute;
        elapsed -= zoneShift;
    }
    return Math.trunc(elapsed / MS_PER[unit]) || 0;
}
